'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import { storyblokEditable } from '@storyblok/react'
import Image from 'next/image'
import { ParallaxStoryblok } from '@/component-types-sb'

interface ParallaxProps {
    blok: ParallaxStoryblok
}

export default function Parallax({ blok }: ParallaxProps) {
    const ref = useRef<HTMLDivElement>(null)
    const [offset, setOffset] = useState(0)
    const [imageLoaded, setImageLoaded] = useState(false)

    // Image container is h-[200%] of section height — use natural aspect ratio,
    // object-cover CSS handles display cropping
    const imageLoader = useCallback(
        ({ src, width, quality }: { src: string; width: number; quality?: number }) => {
            if (!src.includes('a.storyblok.com') || src.endsWith('.svg')) return src
            return `${src}/m/${width}x0/filters:format(webp):quality(${quality ?? 65})`
        },
        []
    )

    useEffect(() => {
        const handleScroll = () => {
            if (!ref.current) return
            const rect = ref.current.getBoundingClientRect()
            const windowHeight = window.innerHeight
            // Only update when element is in viewport
            if (rect.bottom >= 0 && rect.top <= windowHeight) {
                setOffset((rect.top / windowHeight) * 200)
            }
        }
        handleScroll()
        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const height = blok.height || '400px'

    return (
        <section
            {...storyblokEditable(blok)}
            ref={ref}
            className="parallax relative w-full overflow-hidden bg-neutral-800"
            style={{ height: height + 'px' }}
        >
            {blok.image?.filename && (
                <div
                    className="absolute inset-0 w-full h-[200%] -top-[50%]"
                    style={{
                        transform: `translateY(${offset}px)`,
                        willChange: 'transform',
                    }}
                >
                    <Image
                        src={blok.image.filename}
                        loader={imageLoader}
                        alt={blok.image.alt || ''}
                        fill
                        sizes="(max-width: 768px) 100vw, 1200px"
                        className={`object-cover transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                        priority
                        fetchPriority="high"
                        onLoad={() => setImageLoaded(true)}
                    />
                </div>
            )}
            {(blok.heading || blok.text) && (
                <div className="relative max-w-[1200px] mx-auto z-10 flex flex-col items-center justify-center h-full text-center px-5">
                    {blok.heading && (
                        <h2 className="text-white text-xl md:text-3xl font-bold mb-4 drop-shadow-lg">
                            {blok.heading}
                        </h2>
                    )}
                    {blok.text && (
                        <p className="text-white/90 text-lg md:text-xl max-w-2xl drop-shadow-md">
                            {blok.text}
                        </p>
                    )}
                </div>
            )}
            {/* Optional dark overlay */}
            {blok.overlay && (
                <div className="absolute inset-0 bg-black/50 z-[1]" />
            )}
        </section>
    )
}
